"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { type DialogTopic, mergeConsecutiveNpcNodes } from "@/lib/dialog-data";
import { ArrowLeft, BookOpen, GitBranch, GraduationCap, Layers, Trash2, RefreshCw, Pencil, Loader2 } from "lucide-react";
import NextImage from "next/image";
import { VocabularySection } from "./vocabulary-section";
import { PracticeSection } from "./practice/practice-section";
import { DialogTree } from "./dialog-tree";
import { CardsTab } from "./cards-tab";
import { useTranslations } from "@/lib/use-translations";
import { useAnalytics } from "@/lib/use-analytics";
import { GenerationProgress } from "@/components/generation-progress";
import type { GenerationProgress as GenerationProgressData } from "@/hooks/use-generation-service";
import { useLanguage } from "@/lib/language-context";
import type { Richness } from "@/lib/api/client";
import { useSettings } from "@/lib/settings-context";
import { useTtsPrefetch } from "@/lib/hooks/use-tts-prefetch";
import { CoachMarks, type CoachMarkStep } from "@/components/coach-marks";

function Confetti() {
  const colors = ["#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#ffeaa7", "#dfe6e9", "#a29bfe", "#fd79a8"];
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {Array.from({ length: 50 }).map((_, i) => {
        const color = colors[i % colors.length];
        const left = Math.random() * 100;
        const delay = Math.random() * 0.5;
        const duration = 2 + Math.random() * 2;
        const size = 8 + Math.random() * 8;
        return (
          <div
            key={i}
            className="absolute animate-confetti"
            style={{
              left: `${left}%`,
              top: "-20px",
              width: size,
              height: size,
              backgroundColor: color,
              borderRadius: Math.random() > 0.5 ? "50%" : "2px",
              animationDelay: `${delay}s`,
              animationDuration: `${duration}s`,
            }}
          />
        );
      })}
    </div>
  );
}

interface LearningViewProps {
  topic: DialogTopic;
  onBack: () => void;
  onDelete?: (topicId: string) => void;
  onRegenerate?: () => void;
  onEdit?: () => void;
  isStreaming?: boolean;
  streamingProgress?: GenerationProgressData | null;
  streamingError?: string | null;
  richness?: Richness;
  ttsReadyTexts?: string[];
  flyMachineId?: string | null;
}

export function LearningView({ topic, onBack, onDelete, onRegenerate, onEdit, isStreaming, streamingProgress, streamingError, richness, ttsReadyTexts, flyMachineId }: LearningViewProps) {
  const normalizedNodes = useMemo(() => mergeConsecutiveNpcNodes(topic.dialogNodes), [topic.dialogNodes]);
  const [activeTab, setActiveTab] = useState("tree");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [dialogModalOpen, setDialogModalOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();
  const { track, events } = useAnalytics();
  const { nativeLanguage } = useLanguage();
  const { hasSeenLearningWalkthrough, completeLearningWalkthrough } = useSettings();
  const ttsPrefetch = useTtsPrefetch(topic.language, flyMachineId);
  const startTimeRef = useRef<number>(Date.now());
  const hasTrackedStart = useRef(false);

  // Show progress screen until first exchange + vocabulary are ready
  const hasFirstExchange = useMemo(() => {
    const hasStart = topic.dialogNodes.some(n => n?.type === "start");
    const hasOption = topic.dialogNodes.some(n => n?.type === "option");
    return hasStart && hasOption;
  }, [topic.dialogNodes]);

  const hasVocabulary = topic.vocabulary.words.length > 0;

  // Event-driven TTS prefetch: react to BE tts_ready signals.
  // Not gated on isStreaming — tts_ready events can arrive in the same React
  // batch as the "complete" event that flips isStreaming to false.
  useEffect(() => {
    if (!ttsReadyTexts?.length) return;
    ttsPrefetch.markServerReady(ttsReadyTexts);
  }, [ttsReadyTexts, ttsPrefetch.markServerReady]);


  // Gate "Let's Go" on TTS readiness for the first exchange
  const hasTtsForFirstExchange = useMemo(() => {
    const firstNodes = topic.dialogNodes
      .filter((n) => n && (n.type === "start" || n.type === "option"))
      .slice(0, 2);
    return (
      firstNodes.length >= 2 && firstNodes.every((n) => ttsPrefetch.isReady(n.text))
    );
  }, [topic.dialogNodes, ttsPrefetch.isReady]);

  const readyToShow = hasFirstExchange && hasVocabulary && hasTtsForFirstExchange;

  // Show progress screen while streaming, or when there's an error and no content loaded yet
  const showProgressScreen = (isStreaming && !readyToShow) || (!!streamingError && !readyToShow);

  // "Let's Go" celebration screen when first exchange + vocabulary are ready
  const [showCompletion, setShowCompletion] = useState(false);
  const hadReadyRef = useRef(false);
  const wasStreamingRef = useRef(false);

  // Track whether this topic was streamed/generated (latch: once true, stays true)
  useEffect(() => {
    if (isStreaming) {
      wasStreamingRef.current = true;
    }
  }, [isStreaming]);

  // Show celebration when readyToShow becomes true for the first time.
  // Works for both slow streaming (custom generation) and fast completion (adaptation).
  // Gated on wasStreamingRef so pre-generated topics never trigger celebration.
  useEffect(() => {
    if (readyToShow && !hadReadyRef.current && wasStreamingRef.current) {
      hadReadyRef.current = true;
      setShowCompletion(true);
      const id = Date.now();
      setConfettiBursts([id]);
      setTimeout(() => setConfettiBursts(prev => prev.filter(b => b !== id)), 3000);
    }
  }, [readyToShow]);

  const addConfettiBurst = useCallback(() => {
    const id = Date.now();
    setConfettiBursts(prev => [...prev, id]);
    setTimeout(() => setConfettiBursts(prev => prev.filter(b => b !== id)), 3000);
  }, []);
  const [confettiBursts, setConfettiBursts] = useState<number[]>([]);

  // Track dialogue started on mount
  useEffect(() => {
    if (!hasTrackedStart.current) {
      track(events.DIALOGUE_STARTED, {
        topic_id: topic.id,
        title: topic.title,
        difficulty: topic.difficulty,
        node_count: topic.dialogNodes.length,
      });
      hasTrackedStart.current = true;
      startTimeRef.current = Date.now();
    }
  }, [track, events, topic]);

  // Track dialogue completion when user leaves (unmount)
  useEffect(() => {
    return () => {
      const durationSec = Math.round((Date.now() - startTimeRef.current) / 1000);
      track(events.DIALOGUE_COMPLETED, {
        topic_id: topic.id,
        title: topic.title,
        duration_sec: durationSec,
      });
    };
  }, [track, events, topic.id, topic.title]);

  const handleDelete = () => {
    onDelete?.(topic.id);
  };

  const handleRegenerate = () => {
    onRegenerate?.();
  };

  const handleEdit = () => {
    onEdit?.();
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setShowActionsMenu(false);
      }
    };

    if (showActionsMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
    return undefined;
  }, [showActionsMenu]);

  const tabConfig = [
    { value: "tree", icon: GitBranch, label: t.learning.dialogues },
    { value: "cards", icon: Layers, label: t.learning.cards },
    { value: "practice", icon: GraduationCap, label: t.learning.practiceTab || "Practice" },
    { value: "vocab", icon: BookOpen, label: t.learning.vocabulary },
  ];

  const coachMarkSteps: CoachMarkStep[] = useMemo(() => [
    { target: "start-node", title: t.coachMarks.startNodeTitle, body: t.coachMarks.startNodeBody },
    { target: "speaker-button", title: t.coachMarks.speakerTitle, body: t.coachMarks.speakerBody },
    { target: "answer-options", title: t.coachMarks.answerTitle, body: t.coachMarks.answerBody },
    { target: "tabs-navigation", title: t.coachMarks.tabsTitle, body: t.coachMarks.tabsBody, placement: "below" as const },
  ], [t]);

  const showCoachMarks = !hasSeenLearningWalkthrough && !showProgressScreen && !showCompletion;

  // Full-screen progress while waiting for first exchange
  const isAdaptation = topic.id.startsWith("adapt-");

  if (showProgressScreen) {
    return (
      <div className="flex flex-col min-h-screen bg-background">
        {/* Header with back button and title */}
        <div className="px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="shrink-0 -ml-1 w-10 h-10 rounded-xl hover:bg-primary/10 hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="sr-only">{t.learning.back}</span>
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold text-foreground truncate leading-tight">
              {topic.title || (isAdaptation ? "Adapting dialogue" : "Generating dialogue")}
            </h1>
          </div>
        </div>

        {/* Centered progress */}
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-full max-w-lg space-y-6">
            {streamingError ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-1">
                    {streamingError.includes("Failed to fetch") ? "Connection error" : "Something went wrong"}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {streamingError.includes("Failed to fetch")
                      ? "Could not reach the server. It may be starting up — please try again in a moment."
                      : streamingError}
                  </p>
                </div>
                <Button
                  onClick={onBack}
                  variant="outline"
                  className="mt-2"
                >
                  {t.generator?.goBack || "Go Back"}
                </Button>
              </div>
            ) : streamingProgress ? (
              <GenerationProgress
                stage={streamingProgress.stage}
                total={streamingProgress.total}
                stageName={streamingProgress.name}
                nativeLocale={nativeLanguage.code}
                detail={streamingProgress.detail}
                richness={richness}
                streamingNodeCount={topic.dialogNodes.length}
              />
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">{t.generator?.connectingToAI || "Connecting..."}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // "Let's Go!" celebration overlay after generation completes
  if (showCompletion) {
    return (
      <div
        className="flex flex-col min-h-screen bg-background"
        onClick={addConfettiBurst}
      >
        {confettiBursts.map(id => (
          <Confetti key={id} />
        ))}

        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="text-center" style={{ marginTop: '-10vh' }}>
            <div className="w-40 h-40 relative mx-auto rounded-lg overflow-hidden">
              <NextImage
                src="/sponge-bob-rainbow-magic.webp"
                alt="Done!"
                fill
                className="object-cover"
                unoptimized
              />
            </div>
            <h2 className="text-3xl font-bold text-foreground mt-4">{t.generator.done}</h2>

            <Button
              onClick={(e) => {
                e.stopPropagation();
                setShowCompletion(false);
              }}
              className="h-16 px-12 text-xl btn-glow-pulsate mt-12"
            >
              {t.generator.letsGo}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background relative">
        <div className="px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="shrink-0 -ml-1 w-10 h-10 rounded-xl hover:bg-primary/10 hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="sr-only">{t.learning.back}</span>
          </Button>

          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold text-foreground truncate leading-tight">
              {topic.summary || topic.title}
            </h1>
          </div>
        </div>

        {/* Regenerate Dialog */}
          <AlertDialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
            <AlertDialogContent className="glass border-border/60 animate-scale-in">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-lg">
                  {t.topics.regenerateTitle}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-muted-foreground">
                  {t.topics.regenerateDescription}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-2">
                <AlertDialogCancel className="rounded-lg">{t.common?.cancel || "Cancel"}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleRegenerate}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg"
                >
                  {t.topics.regenerate}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Delete Dialog */}
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent className="glass border-border/60 animate-scale-in">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-lg">
                  {t.topics?.deleteTitle || "Delete Topic"}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-muted-foreground">
                  {t.topics?.deleteDescription ||
                    `Are you sure you want to delete "${topic.title}"? This action cannot be undone.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-2">
                <AlertDialogCancel className="rounded-lg">{t.common?.cancel || "Cancel"}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-lg"
                >
                  {t.common?.delete || "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

        {/* Tab Navigation */}
          <TabsList data-coach-mark="tabs-navigation" className="w-full h-14 p-1.5 bg-muted rounded-none justify-start gap-1 overflow-x-auto shadow-[0_4px_6px_-1px_rgba(255,255,255,0.8)] dark:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.3)]">
            {tabConfig.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.value;

              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className={`
                    flex-1 min-w-fit gap-2 rounded-lg h-full
                    transition-all duration-300
                    data-[state=active]:bg-card data-[state=active]:shadow-sm
                    data-[state=active]:border data-[state=active]:border-border/50
                  `}
                >
                  <div
                    className={`
                      w-7 h-7 rounded-md flex items-center justify-center transition-colors
                      ${isActive ? "bg-primary/15" : "bg-transparent"}
                    `}
                  >
                    <Icon
                      className={`w-4 h-4 transition-colors ${
                        isActive ? "text-primary" : "text-muted-foreground"
                      }`}
                    />
                  </div>
                  <span
                    className={`text-xs font-medium transition-colors ${
                      isActive ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    {tab.label}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>
      </header>


      <TabsContent value="tree" className="mt-0 flex-1 animate-fade-up">
        <DialogTree
          nodes={normalizedNodes}
          vocabulary={topic.vocabulary}
          speaker={topic.speaker}
          topicTitle={topic.title}
          traceId={topic.traceId}
          onModalChange={setDialogModalOpen}
          isStreaming={isStreaming}
        />
      </TabsContent>

      <TabsContent value="cards" className="mt-0 flex-1 animate-fade-up">
        <CardsTab nodes={normalizedNodes} selectedChildMap={new Map()} />
      </TabsContent>

      <TabsContent value="practice" className="mt-0 animate-fade-up">
        <PracticeSection vocabulary={topic.vocabulary} language={topic.language} topicTitle={topic.title} topicDescription={topic.description} />
      </TabsContent>

      <TabsContent value="vocab" className="mt-0 animate-fade-up">
        <VocabularySection vocabulary={topic.vocabulary} isStreaming={isStreaming} />
      </TabsContent>
      </Tabs>

      {/* Coach marks walkthrough */}
      {showCoachMarks && (
        <CoachMarks steps={coachMarkSteps} onComplete={completeLearningWalkthrough} />
      )}

      {/* Fixed Actions Menu - Bottom Right (hidden when dialog modal is open) */}
      {(onEdit || onRegenerate || onDelete) && !dialogModalOpen && (
        <div className="fixed bottom-6 right-6 z-40" ref={actionsMenuRef}>
          {/* Flyout Menu */}
          {showActionsMenu && (
            <div className="absolute right-0 bottom-full mb-2 flex flex-col items-end gap-1 animate-in fade-in slide-in-from-bottom-2 duration-150">
              {onEdit && (
                <button
                  onClick={() => {
                    setShowActionsMenu(false);
                    handleEdit();
                  }}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-foreground hover:text-foreground transition-colors whitespace-nowrap rounded-lg bg-gradient-to-l from-background via-background/95 to-background/80 backdrop-blur-sm shadow-sm"
                >
                  <span>{t.topics.edit}</span>
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              {onRegenerate && (
                <button
                  onClick={() => {
                    setShowActionsMenu(false);
                    setShowRegenerateDialog(true);
                  }}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-foreground hover:text-foreground transition-colors whitespace-nowrap rounded-lg bg-gradient-to-l from-background via-background/95 to-background/80 backdrop-blur-sm shadow-sm"
                >
                  <span>{t.topics.regenerate}</span>
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => {
                    setShowActionsMenu(false);
                    setShowDeleteDialog(true);
                  }}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-destructive hover:text-destructive transition-colors whitespace-nowrap rounded-lg bg-gradient-to-l from-background via-background/95 to-background/80 backdrop-blur-sm shadow-sm"
                >
                  <span>{t.common?.delete || "Delete"}</span>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowActionsMenu(!showActionsMenu)}
            className="w-12 h-12 rounded-full bg-muted/80 backdrop-blur-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shadow-xl drop-shadow-lg"
          >
            <Pencil className="w-5 h-5" />
            <span className="sr-only">Actions</span>
          </Button>
        </div>
      )}
    </div>
  );
}
